import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "semantic-ui-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";

import { AccessibleModal } from "../../Accessibility/AccessibleModal";
import { FileChooserDropzoneTrigger } from "./FileChooserDropzoneTrigger";
import { FileUploaderPopup, type FileUploaderProps } from "./FileUploaderPopup";
import { allowedFileTypesList } from "./uploadUtils";

type FileChooserProps = FileUploaderProps & {
  trigger?: React.ReactElement;
  content?: string;
  isBasic?: boolean;
  primary?: boolean;
  secondary?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

export const FileChooser = ({
  isOpen,
  onOpen,
  onClose,
  onComplete,
  onCompleteMultiple,
  trigger,
  isBasic,
  primary,
  secondary,
  content,
  disabled,
  dropzoneProps,
  ...uploaderProps
}: FileChooserProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose();
    }
    setOpen(false);
  }, [open, onClose]);

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup();
    }
    if (!open && onOpen) onOpen();
    setOpen(true);
  }, [open, onOpen, closePopup]);

  const onChosen = useCallback(
    (responseFilename: string, filename: string, responseDownload: string) => {
      onComplete(responseFilename, filename, responseDownload);
      closePopup();
    },
    [onComplete, closePopup],
  );
  const onChosenMultiple = useCallback(
    (files) => {
      onCompleteMultiple(files);
      closePopup();
    },
    [onCompleteMultiple, closePopup],
  );

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const triggerElement = useMemo(() => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
        onKeyDown: handleClickForKeyboard(disabled ? () => {} : openPopup),
      });
    }

    return (
      <Button
        size="small"
        primary={primary}
        secondary={secondary}
        basic={isBasic}
        icon="image"
        disabled={disabled}
        content={content || t("ideas.details.chooseCover")}
        onClick={openPopup}
      />
    );
  }, [trigger, openPopup, disabled, isBasic, content, t, primary, secondary]);

  return (
    <>
      {triggerElement}
      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
      >
        <AccessibleModal.Header>Choose a file</AccessibleModal.Header>
        <AccessibleModal.Content>
          <h3>{t("ideas.actions.chooseImage.info")}</h3>
          <FileUploaderPopup
            onComplete={onChosen}
            onCompleteMultiple={onChosenMultiple}
            onError={toast.error}
            accept={allowedFileTypesList.join(",")}
            dropzoneProps={dropzoneProps}
            {...uploaderProps}
          />
        </AccessibleModal.Content>
        <AccessibleModal.Actions>
          <Button content={t("generic.close")} onClick={closePopup} />
        </AccessibleModal.Actions>
      </AccessibleModal>
    </>
  );
};

type FileChooserWithDropzoneProps = Omit<FileChooserProps, "trigger">;

export const FileChooserWithDropzone = (
  props: FileChooserWithDropzoneProps,
) => {
  return (
    <FileChooser
      trigger={<FileChooserDropzoneTrigger {...props} />}
      {...props}
    />
  );
};
